import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@tyro/i18n';
import { ClockIcon } from '@tyro/icons';
import { useState } from 'react';
import type { RecentlyViewedDoc } from '../../hooks/use-recently-viewed-docs';
import { RecentlyViewedRow } from './recently-viewed-row';

interface ReaderRecentlyViewedSectionProps {
  entries: RecentlyViewedDoc[];
  collapsedLimit: number;
  onClear: () => void;
  onSelectPage: (spaceId: number, pageSlug: string) => void;
}

export function ReaderRecentlyViewedSection({
  entries,
  collapsedLimit,
  onClear,
  onSelectPage,
}: ReaderRecentlyViewedSectionProps) {
  const { t } = useTranslation(['documentation']);
  const [showAll, setShowAll] = useState(false);
  const canCollapse = entries.length > collapsedLimit;
  const visibleEntries =
    canCollapse && !showAll ? entries.slice(0, collapsedLimit) : entries;

  return (
    <Stack gap={1.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ minHeight: 24 }}
      >
        <Stack direction="row" alignItems="center">
          <ClockIcon
            sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              px: 0.5,
            }}
          >
            {t('documentation:reader.recentlyViewed')}
          </Typography>
        </Stack>
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          color="primary"
          onClick={onClear}
          sx={{ fontWeight: 600, px: 0.5 }}
        >
          {t('documentation:reader.clearRecentlyViewed')}
        </Link>
      </Stack>
      <Card>
        <Stack divider={<Divider />}>
          {visibleEntries.map((entry) => (
            <RecentlyViewedRow
              key={entry.pageId}
              entry={entry}
              onSelectPage={onSelectPage}
            />
          ))}
        </Stack>
      </Card>
      {canCollapse && (
        <Box sx={{ pl: 0.5 }}>
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            color="primary"
            onClick={() => setShowAll((value) => !value)}
            sx={{ fontWeight: 600 }}
          >
            {showAll
              ? t('documentation:reader.showLess')
              : t('documentation:reader.showMore', {
                  count: entries.length - collapsedLimit,
                })}
          </Link>
        </Box>
      )}
    </Stack>
  );
}
